import { useState, type FormEvent } from "react";
import Reveal from "@/components/base/Reveal";

const FORM_ID = "dau03a1ggrtvtk1bg7vg";
const SUBMIT_ADDR = "https://readdy.ai/api/form/dau03a1ggrtvtk1bg7vg";

type Status = "idle" | "submitting" | "success" | "error";

export default function BlogNewsletter() {
  const [status, setStatus] = useState<Status>("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("Inscrição registrada. Obrigado!");
      form.reset();
      return;
    }

    const params = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "contact_alt") return;
      const text = String(value).trim();
      if (text) params.append(key, text);
    });

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      if (
        !response.ok ||
        parsed?.code !== "OK" ||
        serverMsg.toLowerCase().includes("spam")
      ) {
        setStatus("error");
        setFeedback(serverMsg || "Não foi possível concluir a inscrição. Tente novamente.");
        return;
      }

      setStatus("success");
      setFeedback("Tudo certo! Você vai receber os próximos conteúdos da NXHOST.");
      form.reset();
    } catch {
      setStatus("error");
      setFeedback("Erro de conexão. Verifique sua internet e tente novamente.");
    }
  };

  return (
    <section className="bg-foreground-950 py-16 text-background-50 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <Reveal>
          <div className="mx-auto max-w-3xl rounded-2xl border border-background-50/10 bg-background-50/5 p-6 text-center md:p-10">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-accent-300">
              <i className="ri-mail-send-line" aria-hidden="true"></i>
              Newsletter NXHOST
            </span>
            <h2 className="mt-5 font-heading text-2xl font-bold md:text-3xl">
              Receba novos conteúdos da NXHOST
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-background-200 md:text-base">
              Dicas de tecnologia, sites, hospedagem, SEO e marketing diretamente no seu e-mail.
            </p>

            <form
              id={FORM_ID}
              data-readdy-form
              onSubmit={handleSubmit}
              className="mx-auto mt-8 flex w-full max-w-xl flex-col gap-2 sm:flex-row"
            >
              <div className="nx-honeypot-field" aria-hidden="true">
                <label htmlFor={`${FORM_ID}-contact-alt`}>Não preencha</label>
                <input
                  id={`${FORM_ID}-contact-alt`}
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <input
                type="email"
                name="email"
                required
                placeholder="Seu melhor e-mail"
                aria-label="Seu melhor e-mail"
                className="min-w-0 flex-1 rounded-md border border-background-50/15 bg-background-50/5 px-4 py-3.5 text-sm text-background-50 outline-none transition-colors placeholder:text-background-400 focus:border-accent-300"
              />
              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className="ri-send-plane-line" aria-hidden="true"></i>
                {status === "submitting" ? "Enviando..." : "Quero receber"}
              </button>
            </form>

            {feedback ? (
              <p
                role="status"
                className={`mt-4 text-sm ${
                  status === "error" ? "text-accent-300" : "text-secondary-300"
                }`}
              >
                {feedback}
              </p>
            ) : null}

            <p className="mt-4 text-xs text-background-400">
              Sem spam. Você pode cancelar a inscrição quando quiser.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}